import {
  EntitySubType,
  getSortedHazardFeatures,
  HAZARD_MARKERS,
  HazardType,
  MapboxSource,
  MapMode,
  SelectedHazardViewType,
  useHazardsStore,
} from '@/features/hazards';
import { LngLatBoundsLike, LngLatLike, Map as Mapbox, Marker } from 'mapbox-gl';
import { Ref } from 'vue';

import { Feature, GeoJsonProperties, Geometry, Point } from 'geojson';
import { generateChecksum } from '@/common';
import { bbox } from '@turf/turf';

const markerElement = (src: string, badge?: number) => {
  const element = document.createElement('image');
  element.className = 'marker size-8 cursor-pointer';

  const image = document.createElement('img');
  image.setAttribute('alt', badge ? 'cluster marker' : 'hazard marker');
  image.setAttribute('src', src);
  element.appendChild(image);

  if (badge) {
    const badgeDiv = document.createElement('div');
    badgeDiv.className =
      'font-proxima-nova text-2xs font-bold text-white absolute top-0 size-4 rounded-full bg-indigo-500 text-center content-end';
    badgeDiv.innerText = badge.toString();
    element.appendChild(badgeDiv);
  }

  return element;
};

const generateClusterClickHandler = (mapbox: Ref<Mapbox | null>, leaves: Feature<Geometry, GeoJsonProperties>[]) => (event: any) => {
  event.preventDefault();
  event.stopPropagation();

  const bounds = bbox({
    type: 'FeatureCollection',
    features: leaves,
  });

  mapbox.value?.fitBounds(bounds as LngLatBoundsLike, {
    padding: { top: 20, bottom: 90, left: 20, right: 80 },
    speed: 5,
  });
};

const generateFeatureClickHandler =
  (eventId: string, hazardId: string, hazardType: HazardType, hazards: ReturnType<typeof useHazardsStore>) => async (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    hazards.toggleSelectHazard({ hazardId, eventId, hazardType, viewType: SelectedHazardViewType.Popup });
  };

const syncMarkerCoordinates = (markers: Map<string, Marker>, features: Feature<Geometry, GeoJsonProperties>[]) => {
  const featureMap = new Map(
    features.map((f) => {
      const props = f.properties!;
      const featureId = props.timeAwareFeatureId ?? props.featureId ?? props.id;
      return [featureId, f];
    })
  );

  for (const [id, marker] of markers) {
    const feature = featureMap.get(id);

    if (feature) {
      const featureCoords = (feature.geometry as Point).coordinates;
      const markerLngLat = marker.getLngLat();

      if (markerLngLat.lng !== featureCoords[0] || markerLngLat.lat !== featureCoords[1]) {
        marker.setLngLat([featureCoords[0], featureCoords[1]]);
      }
    }
  }
};

/**
 * Manages the markers on the mapbox.value.
 * Clears all markers if the map mode is not {@link MapMode.Default}.
 * Renders the raw DOM markeers built from above, with the click handler dependant on the type
 *
 * Uses `markers` and `clusterMarkersOnScreen` to optimise the rendering by reusing
 * existing markers.
 *
 * Derived from https://docs.mapbox.com/mapbox-gl-js/example/cluster-html/
 */
export const updateMarkers = async (
  mapbox: Ref<Mapbox | null>,
  clusterMarkers: Map<string, Marker>,
  clusterMarkersOnScreen: Map<string, Marker>
) => {
  const hazards = useHazardsStore();

  /// Clear all markers if the map mode is not Default.
  if (!mapbox.value || hazards.mapMode !== MapMode.Default) {
    clusterMarkersOnScreen.forEach((marker) => marker.remove());
    clusterMarkers.clear();
    return new Map<string, Marker>();
  }
  const newMarkers = new Map<string, Marker>();
  const source = mapbox.value.getSource(MapboxSource.hazardsMarkersSource);
  if (!source || source.type != 'geojson') return clusterMarkersOnScreen;

  const clusterFeatures = mapbox.value.querySourceFeatures(MapboxSource.hazardsMarkersSource);

  // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
  // and add it to the map if it's not there already
  for (const feature of clusterFeatures) {
    if (hazards.mapMode !== MapMode.Default) break;

    const coords = (feature.geometry as Point).coordinates as LngLatLike;
    const props = feature.properties!;

    // Non cluster markers
    if (!props.cluster) {
      const markerId = props.timeAwareFeatureId ?? props.featureId ?? props.id;
      let marker = clusterMarkers.get(markerId);
      if (!marker) {
        const src = HAZARD_MARKERS[props['icon-name'] as keyof typeof HAZARD_MARKERS];
        const element = markerElement(src);

        element.onclick = generateFeatureClickHandler(props.eventId, props.id, props.entityType, hazards);

        marker = new Marker({ element }).setLngLat(coords);
        clusterMarkers.set(markerId, marker);
      }

      newMarkers.set(markerId, marker);
      if (!clusterMarkersOnScreen.has(markerId)) marker.addTo(mapbox.value);
      continue;
    }

    // Cluster markers
    let clusterId = props.cluster_id;

    // style changes can balk this completely, add a get out of jail free timeout
    // when there is an issue, this gets re-called anyway because the map does a rebuild
    // when the style change gets upset
    const clusterLeaves = await new Promise<GeoJSON.Feature<GeoJSON.Geometry>[]>((resolve) => {
      const timer = setTimeout(() => resolve([]), 100); // this has worked fine with 2ms, but best to give it a chance

      // note that this is actually a sync call, despite requiring the callback
      // I checked the source code
      source.getClusterLeaves(clusterId, Infinity, 0, (error, leaves) => {
        clearTimeout(timer);
        resolve(error ? [] : leaves);
      });
    });

    const sortedClusterHazards = getSortedHazardFeatures(clusterLeaves);

    if (sortedClusterHazards.length < 1) continue;

    // Compose the clusterId from it's leaves (IDs) to prevent collisions
    clusterId = await generateChecksum(
      sortedClusterHazards.map((hazard) => hazard.properties?.timeAwareFeatureId ?? hazard.properties?.featureId ?? hazard.id).join(',')
    );

    let marker = clusterMarkers.get(clusterId);

    if (!marker) {
      const src = HAZARD_MARKERS[sortedClusterHazards[0].properties?.['icon-name'] as keyof typeof HAZARD_MARKERS];
      const element = markerElement(src, sortedClusterHazards.length);

      element.onclick = generateClusterClickHandler(mapbox, clusterLeaves);

      marker = new Marker({ element }).setLngLat(coords);
      clusterMarkers.set(clusterId, marker);
    }

    newMarkers.set(clusterId, marker);
    if (!clusterMarkersOnScreen.has(clusterId)) marker.addTo(mapbox.value);
  }

  if (hazards.mapMode !== MapMode.Default) {
    newMarkers.forEach((marker) => marker.remove());
    clusterMarkersOnScreen.forEach((marker) => marker.remove());
    clusterMarkers.clear();
    return new Map<string, Marker>();
  }

  // Remove previously added markers that are no longer visible.
  clusterMarkersOnScreen.forEach((marker, id) => {
    if (newMarkers.has(id)) return;

    marker.remove();
  });

  // Processing only earthquake incidents separately to sync their coordinates for now.
  // This can be extended to other incident types if needed in the future.
  const earthquakeFeatures = clusterFeatures.filter((f) => f.properties?.entitySubType === EntitySubType.EarthquakeIncidentEntity);

  syncMarkerCoordinates(newMarkers, earthquakeFeatures);

  return newMarkers;
};
